/**
 * Dojo Battle View
 *
 * Simple battle-only view for training matches.
 * No draft, no rewards, just pure battle practice.
 */

import { useCallback, useEffect, useRef, useState, useMemo } from "react";

import { Application, Container, Sprite, Assets } from "pixi.js";

import { BattleStatsHUD } from "@/components/battle/BattleStatsHUD";
import { KillAnnouncer } from "@/components/battle/KillAnnouncer";
import { PokemonBattleHUD } from "@/components/battle/PokemonBattleHUD";
import { EnvironmentalEffects } from "@/components/environment/environmental-effects";
import { NightOverlay } from "@/components/environment/lighting-effects";
import { Button } from "@/components/ui/buttons/button";
import { useBackAction } from "@/hooks/use-back-action";
import {
  buildAssetManifest,
  preloadBattleAssets,
} from "@/lib/battle/asset-loader";
import { BattleReplay } from "@/lib/battle/BattleReplay";
import { ClientPokemon } from "@/lib/battle/entities/client-pokemon";
import { ClientDummyTarget } from "@/lib/battle/entities/client-dummy-target";
import { useSPAContext } from "@/providers/spa-provider";
import { RouteNames } from "@/types/route-names";
import { PokemonSprite } from "@/components/pokemon/pokemon-sprite";

import { POKEMON_SKILLS } from "game/data/skills/POKEMON_SKILLS";
import type { KillEvent } from "@/components/battle/KillAnnouncer";
import type { CompleteBattleLog } from "types/battle-engine";
import type { Battle_Action } from "types/battle-engine/battle";

const MS_PER_TICK = 50;

type BattlePhase = "loading" | "ready" | "playing" | "completed";

interface DojoBattleViewProps {
  battleLog: CompleteBattleLog;
  botName: string;
  botCountry: string | null;
  onBack: () => void;
  onRematch: () => void;
}

export const DojoBattleView = ({
  battleLog,
  botName,
  botCountry,
  onBack,
  onRematch,
}: DojoBattleViewProps) => {
  const { setCurrentRoute } = useSPAContext();

  const containerRef = useRef<HTMLDivElement>(null);
  const [app, setApp] = useState<Application | null>(null);
  const [world, setWorld] = useState<Container | null>(null);
  const [pokemons, setPokemons] = useState<Record<string, ClientPokemon>>({});
  const [battlePhase, setBattlePhase] = useState<BattlePhase>("loading");
  const [currentTick, setCurrentTick] = useState(0);
  const [killEvents, setKillEvents] = useState<KillEvent[]>([]);
  const [selectedPokemonId, setSelectedPokemonId] = useState<string | null>(
    null,
  );
  const [battleResult, setBattleResult] = useState<{
    isPlayerWinner: boolean;
    isDraw: boolean;
  } | null>(null);

  const replayRef = useRef<BattleReplay | null>(null);

  const pokemonIds = Object.keys(pokemons);

  // Calculate battle stats for the result screen
  const battleStats = useMemo(() => {
    if (!battleLog || !battleLog.teamA.members[0]) return null;

    const playerMember = battleLog.teamA.members[0];
    const playerId = playerMember.pokemon.id;
    const stats = battleLog.stats[playerId];

    if (!stats) return null;

    const durationSeconds = (battleLog.total_ticks * MS_PER_TICK) / 1000;
    const totalDamage = stats.damage_done;
    const dps = durationSeconds > 0 ? totalDamage / durationSeconds : 0;

    // Calculate Max Hit
    let maxHit = 0;
    // We need to look at events to find max hit.
    // Note: The events in battleLog are raw events.
    // We need to filter for DAMAGE events caused by player.
    // However, BattleEvent type structure in frontend might be slightly different or we need to cast.
    // Let's assume standard structure based on usage in BattleReplay.

    // Iterate over all events to find max damage instance
    // We can also count skills used
    let skillsUsed = 0;

    // Parse events if available
    const damagePerSkill: Record<string, number> = {};

    if (battleLog.events) {
      battleLog.events.forEach((event: any) => {
        if (event.event_type === "DAMAGE") {
          const targetId = event.target_id;
          // Check if target is dummy (Team B)
          // target_id is string in event, but pokemon.id is number
          const isTargetDummy = battleLog.teamB.members.some(
            (m) => m.pokemon.id === Number(targetId),
          );

          if (isTargetDummy) {
            if (event.amount > maxHit) {
              maxHit = event.amount;
            }

            if (event.skill_id) {
              const skillId = event.skill_id;
              damagePerSkill[skillId] =
                (damagePerSkill[skillId] || 0) + event.amount;
            } else {
              damagePerSkill["basic_attack"] =
                (damagePerSkill["basic_attack"] || 0) + event.amount;
            }
          }
        }
      });
    }

    // Format damage per skill for display
    const damageBreakdown = Object.entries(damagePerSkill)
      .map(([skillId, damage]) => {
        const skillName =
          skillId === "basic_attack"
            ? "Basic Attack"
            : POKEMON_SKILLS[skillId as keyof typeof POKEMON_SKILLS]?.name ??
              skillId;
        return { name: skillName, damage: Math.round(damage) };
      })
      .sort((a, b) => b.damage - a.damage);

    // Count skills from actions
    playerMember.actions.forEach((action) => {
      if (action.type === "USE_SKILL") {
        skillsUsed++;
      }
    });

    return {
      pokemon: playerMember.pokemon,
      duration: durationSeconds.toFixed(1),
      totalDamage: Math.round(totalDamage),
      dps: Math.round(dps),
      maxHit: Math.round(maxHit),
      skillsUsed,
      totalHealing: Math.round(stats.healing_done),
      damageBreakdown,
    };
  }, [battleLog]);

  // Initial selection
  useEffect(() => {
    if (!selectedPokemonId && pokemonIds.length > 0) {
      setSelectedPokemonId(pokemonIds[0] ?? null);
    }
  }, [pokemonIds, selectedPokemonId]);

  const handleNext = useCallback(() => {
    setSelectedPokemonId((prev) => {
      if (!prev) return pokemonIds[0] ?? null;
      const currentIndex = pokemonIds.indexOf(prev);
      const nextIndex = (currentIndex + 1) % pokemonIds.length;
      return pokemonIds[nextIndex] ?? null;
    });
  }, [pokemonIds]);

  const handlePrevious = useCallback(() => {
    setSelectedPokemonId((prev) => {
      if (!prev) return pokemonIds[pokemonIds.length - 1] ?? null;
      const currentIndex = pokemonIds.indexOf(prev);
      const prevIndex =
        (currentIndex - 1 + pokemonIds.length) % pokemonIds.length;
      return pokemonIds[prevIndex] ?? null;
    });
  }, [pokemonIds]);

  useBackAction(onBack);

  // Initialize PixiJS application
  useEffect(() => {
    let localApp: Application | null = null;
    let isCancelled = false;

    const initApp = async () => {
      const gameContainer = containerRef.current;
      if (!gameContainer || isCancelled) return;
      if (gameContainer.querySelector("canvas")) return;

      const appInstance = new Application();
      await appInstance.init({
        resizeTo: gameContainer,
        backgroundColor: 0x1a1a2e,
        antialias: true,
        resolution: window.devicePixelRatio || 1,
        autoDensity: true,
      });

      if (isCancelled) {
        appInstance.destroy(true);
        return;
      }

      gameContainer.appendChild(appInstance.canvas);
      appInstance.stage.sortableChildren = true;
      localApp = appInstance;

      const worldContainer = new Container();
      worldContainer.sortableChildren = true;
      appInstance.stage.addChild(worldContainer);

      // Setup camera/scaling - fit map (480x960) to canvas, max scale 1
      const mapWidth = 480;
      const mapHeight = 960;
      const scaleX = gameContainer.clientWidth / mapWidth;
      const scaleY = gameContainer.clientHeight / mapHeight;
      const scale = Math.min(scaleX, scaleY, 1);

      worldContainer.scale.set(scale);
      worldContainer.position.set(
        (gameContainer.clientWidth - mapWidth * scale) / 2,
        (gameContainer.clientHeight - mapHeight * scale) / 2,
      );

      setApp(appInstance);
      setWorld(worldContainer);
    };

    void initApp();

    return () => {
      isCancelled = true;
      if (localApp) {
        localApp.destroy(true, { children: true });
      }
      setApp(null);
      setWorld(null);
    };
  }, []);

  // Load assets and create pokemons when app and world are ready
  useEffect(() => {
    if (!app || !world || !battleLog) return;

    let isCancelled = false;

    const setupBattle = async () => {
      setBattlePhase("loading");

      // Clean world before setting up a new battle (avoid duplicated sprites/backgrounds)
      if (world) {
        while (world.children.length > 0) {
          const child = world.children[0];
          if (!child) break;
          world.removeChild(child);
          child.destroy({ children: true });
        }
      }

      // Load map background
      const mapId = battleLog.mapId || "battle_arena_1";
      try {
        // Try multiple formats
        let mapTexture;
        try {
          mapTexture = await Assets.load(`/assets/maps/${mapId}.webp`);
        } catch {
          try {
            mapTexture = await Assets.load(`/assets/maps/${mapId}.jpg`);
          } catch {
            mapTexture = await Assets.load(`/assets/maps/${mapId}.png`);
          }
        }
        const mapSprite = new Sprite(mapTexture);
        mapSprite.zIndex = 0;
        mapSprite.width = 480;
        mapSprite.height = 960;
        world.addChild(mapSprite);
      } catch (error) {
        console.warn("Could not load map:", error);
        // Fallback: create a dark background sprite
        const fallbackBg = new Sprite();
        fallbackBg.width = 480;
        fallbackBg.height = 960;
        fallbackBg.tint = 0x1a1a2e;
        fallbackBg.zIndex = 0;
        world.addChild(fallbackBg);
      }

      if (isCancelled) return;

      // Build asset manifest and preload
      const manifest = buildAssetManifest(
        battleLog.teamA.members.map((m) => m.pokemon),
        battleLog.teamB.members.map((m) => m.pokemon),
      );
      await preloadBattleAssets(manifest, () => {});

      if (isCancelled) return;

      // Create ClientPokemons using battleData and initialPosition from battleLog
      const newPokemons: Record<string, ClientPokemon> = {};
      const initPromises: Promise<void>[] = [];

      // Team A
      for (const member of battleLog.teamA.members) {
        const pokemon = new ClientPokemon(
          member.battleData,
          member.initialPosition,
          "A",
          app,
          world,
        );
        newPokemons[member.pokemon.id] = pokemon;
        initPromises.push(pokemon.load());
      }

      // Team B
      for (const member of battleLog.teamB.members) {
        let pokemon: ClientPokemon;
        if (member.battleData.base_pokemon_name === "training-dummy") {
          pokemon = new ClientDummyTarget(
            member.battleData,
            member.initialPosition,
            "B",
            app,
            world,
          );
        } else {
          pokemon = new ClientPokemon(
            member.battleData,
            member.initialPosition,
            "B",
            app,
            world,
          );
        }
        newPokemons[member.pokemon.id] = pokemon;
        initPromises.push(pokemon.load());
      }

      // Wait for all pokemon to load
      await Promise.all(initPromises);

      // Setup interaction for pokemons (click to view details)
      Object.values(newPokemons).forEach((p) => {
        p.setOnClick((clickedPokemon) => {
          console.log("Clicked pokemon:", clickedPokemon.id);
          setSelectedPokemonId(clickedPokemon.id.toString());
        });
      });

      if (isCancelled) return;

      setPokemons(newPokemons);
      setBattlePhase("ready");
    };

    void setupBattle();

    return () => {
      isCancelled = true;
    };
  }, [app, world, battleLog]);

  // Auto-play battle after ready phase
  useEffect(() => {
    if (battlePhase === "ready" && Object.keys(pokemons).length > 0) {
      const timer = setTimeout(() => {
        setBattlePhase("playing");
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [battlePhase, pokemons]);

  // Play battle replay
  useEffect(() => {
    if (
      battlePhase !== "playing" ||
      !battleLog ||
      Object.keys(pokemons).length === 0
    )
      return;
    if (replayRef.current) return;

    let isCancelled = false;

    const play = async () => {
      setKillEvents([]);
      const routines: Record<string, Battle_Action[]> = {};
      [...battleLog.teamA.members, ...battleLog.teamB.members].forEach((m) => {
        routines[m.pokemon.id] = m.actions;
      });

      let lastTickUpdate = 0;
      const replay = new BattleReplay(
        pokemons,
        (tick) => {
          const now = Date.now();
          if (now - lastTickUpdate > 100) {
            setCurrentTick(tick);
            lastTickUpdate = now;
          }
        },
        (event) => {
          if (event.event_type === "KILL") {
            const killer = pokemons[event.killer_id];
            const victim = pokemons[event.victim_id];

            if (killer && victim) {
              const newKillEvent: KillEvent = {
                id: `${event.tick}-${event.killer_id}-${event.victim_id}`,
                killerName: killer.Name,
                victimName: victim.Name,
                killerTeam: killer.Team,
                victimTeam: victim.Team,
                killerBaseName: killer.base_pokemon_name,
                victimBaseName: victim.base_pokemon_name,
              };
              setKillEvents((prev) => [...prev, newKillEvent]);
            }
          }
        },
      );
      replayRef.current = replay;

      try {
        await replay.play(
          routines,
          battleLog.statsPerTick,
          battleLog.events,
          battleLog.mana,
        );
      } catch (e) {
        console.error("[DojoBattle] Battle replay error:", e);
      } finally {
        if (replayRef.current === replay) {
          replayRef.current = null;
        }
      }

      if (isCancelled) return;

      // Determine winner (player is always Team A in training)
      const winner = battleLog.winner;
      let playerWon = false;
      let isDraw = false;
      if (winner === "teamA") {
        playerWon = true;
      } else if (winner === "teamB") {
        playerWon = false;
      } else {
        isDraw = true;
      }

      setBattleResult({ isPlayerWinner: playerWon, isDraw });
      setBattlePhase("completed");
    };

    void play();

    return () => {
      isCancelled = true;
      if (replayRef.current) {
        replayRef.current.destroy();
        replayRef.current = null;
      }
    };
  }, [battlePhase, battleLog, pokemons]);

  const handleExit = useCallback(() => {
    setCurrentRoute(RouteNames.DOJO);
  }, [setCurrentRoute]);

  return (
    <div className="relative bg-pk-night-1 w-full h-full">
      {/* PixiJS Canvas Container */}
      <div ref={containerRef} className="absolute inset-0" />

      {/* Environmental Effects */}
      <EnvironmentalEffects weather={"clouds"} className="opacity-70 !z-150" />
      <NightOverlay />

      {/* Header */}
      <div className="top-0 right-0 left-0 z-20 absolute p-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <span className="bg-pk-red-1 px-2 py-1 rounded font-bold text-white text-sm">
              🥋 TRAINING
            </span>
          </div>
          <div className="flex items-center gap-2 text-white">
            <span className="font-bold">VS</span>
            <span className="text-pk-yellow-1">{botName}</span>
            {botCountry && <span className="text-sm">({botCountry})</span>}
          </div>
        </div>
      </div>

      {/* Battle Stats HUD */}
      {(battlePhase === "playing" || battlePhase === "ready") && (
        <>
          <BattleStatsHUD
            battleState={pokemons}
            battleStats={battleLog.statsPerTick}
            currentTick={currentTick}
          />

          {/* Selected Pokemon Detailed HUD */}
          {selectedPokemonId && pokemons[selectedPokemonId] && (
            <PokemonBattleHUD
              pokemon={pokemons[selectedPokemonId]}
              battleLog={battleLog}
              currentTick={currentTick}
              onClose={() => setSelectedPokemonId(null)}
              onNext={handleNext}
              onPrevious={handlePrevious}
            />
          )}
        </>
      )}

      {/* Kill Announcer */}
      {battlePhase === "playing" && <KillAnnouncer events={killEvents} />}

      {/* Loading Overlay */}
      {battlePhase === "loading" && (
        <div className="z-30 absolute inset-0 flex justify-center items-center bg-pk-night-1/80 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-4">
            <div className="border-4 border-pk-yellow-1 border-t-transparent rounded-full w-12 h-12 animate-spin" />
            <span className="font-bold text-white text-lg">
              Preparing Battle...
            </span>
          </div>
        </div>
      )}

      {/* Battle Result Overlay */}
      {battlePhase === "completed" && battleStats && (
        <div className="z-[200] absolute inset-0 flex justify-center items-center p-6 pointer-events-none">
          <div className="flex flex-col gap-4 bg-pk-night-2/95 border-2 border-white/10 rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl max-h-full pointer-events-auto">
            {/* Header */}
            <div className="w-full bg-black/40 p-3 border-b border-white/10 flex flex-col items-center shrink-0">
              <h2 className="text-pk-yellow-1 font-black text-lg uppercase tracking-widest drop-shadow-md">
                Test Results
              </h2>
              <div className="text-pk-gray-4 text-[10px] uppercase tracking-widest font-bold">
                Simulation Complete
              </div>
            </div>

            {/* Pokemon Info - 2 Column Layout */}
            <div className="px-6 grid grid-cols-[auto_1fr] gap-5 items-center shrink-0">
              {/* Left: Sprite */}
              <div className="w-32 h-32 rounded-xl bg-pk-night-1 border border-white/10 flex items-center justify-center shadow-lg relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent" />
                <PokemonSprite
                  pokemon={battleStats.pokemon}
                  className="w-28 h-28"
                  style={{ scale: 2 }}
                  ignoreScale={true}
                />
              </div>

              {/* Right: Info */}
              <div className="flex flex-col justify-center h-full gap-1">
                <div className="text-white font-black text-3xl leading-tight break-words">
                  {battleStats.pokemon.name ||
                    battleStats.pokemon.base_pokemon_name}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-pk-gray-4 font-bold uppercase text-sm tracking-wider">
                    {battleStats.pokemon.base_pokemon_name}
                  </span>
                  <span className="bg-white/10 px-2 py-0.5 rounded text-xs font-bold text-white">
                    Lv. {battleStats.pokemon.level}
                  </span>
                </div>
              </div>
            </div>

            {/* Metrics Grid */}
            <div className="w-full px-6 grid grid-cols-3 gap-2 shrink-0">
              <MetricCard
                label="Total Damage"
                value={battleStats.totalDamage.toLocaleString()}
                color="text-pk-red-1"
              />
              <MetricCard
                label="DPS"
                value={battleStats.dps.toLocaleString()}
                sublabel="Damage / Sec"
                color="text-pk-yellow-1"
              />
              <MetricCard
                label="Max Hit"
                value={battleStats.maxHit.toLocaleString()}
                color="text-orange-400"
              />
              <MetricCard
                label="Duration"
                value={`${battleStats.duration}s`}
                color="text-blue-400"
              />
              <MetricCard
                label="Skills Used"
                value={battleStats.skillsUsed.toString()}
                color="text-purple-400"
              />
              <MetricCard
                label="Total Healing"
                value={battleStats.totalHealing.toLocaleString()}
                color="text-green-400"
              />
            </div>

            {/* Damage Breakdown */}
            {battleStats.damageBreakdown &&
              battleStats.damageBreakdown.length > 0 && (
                <div className="w-full px-6 mt-2 flex-1 min-h-0 flex flex-col">
                  <div className="text-pk-gray-4 text-xs uppercase tracking-widest font-bold mb-2 text-center shrink-0">
                    Damage Breakdown
                  </div>
                  <div className="flex flex-col gap-1 overflow-y-auto pr-1 custom-scrollbar pb-2">
                    {battleStats.damageBreakdown.map(
                      (item: any, idx: number) => (
                        <div
                          key={idx}
                          className="flex justify-between items-center bg-black/20 p-2 rounded border border-white/5 text-xs"
                        >
                          <span className="text-white font-medium">
                            {item.name}
                          </span>
                          <span className="text-red-400 font-bold">
                            {item.damage.toLocaleString()}
                          </span>
                        </div>
                      ),
                    )}
                  </div>
                </div>
              )}

            {/* Footer Actions */}
            <div className="w-full p-6 pt-2 flex gap-3 shrink-0">
              <Button
                onClick={onRematch}
                variant="yellow"
                className="flex-1 py-3"
              >
                <span className="font-bold tracking-wider">REMATCH</span>
              </Button>
              <Button onClick={onBack} variant="night" className="flex-1 py-3">
                <span className="font-bold tracking-wider">EXIT</span>
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const MetricCard = ({
  label,
  value,
  sublabel,
  color = "text-white",
}: {
  label: string;
  value: string;
  sublabel?: string;
  color?: string;
}) => (
  <div className="bg-black/30 border border-white/5 rounded-lg p-3 flex flex-col items-center justify-center">
    <div className="text-pk-gray-4 text-[10px] uppercase tracking-widest font-bold mb-1">
      {label}
    </div>
    <div className={`font-black text-xl ${color} drop-shadow-sm`}>{value}</div>
    {sublabel && (
      <div className="text-white/40 text-[9px] uppercase tracking-wider mt-0.5">
        {sublabel}
      </div>
    )}
  </div>
);
